import React, { useEffect, useMemo, useState } from "react";
import { Layout, Menu, Button, Divider } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTheme } from "../../../contexts/ThemeContext";
import { generateMenuItems, resolveSelectedMenuKey } from "../../items/MenuItems/MenuItem";
import UserProfile from "../../Dropdowns/UserProfile/UserProfile";
import { useReportBuilder } from "../../../contexts/ReportBuilderContext.jsx";
import CustomModal from "../../modal/CustomModal.jsx";
import NovoLogo from '../../../assets/svg/LogoSimpAzul.svg';
import d4cLogoFull from '../../../assets/svg/LogoCor.svg';

const { Sider } = Layout;

const SimpleSider = ({
  routes,
  location,
  navigate,
  openKeys,
  setOpenKeys,
  collapsed,
  setCollapsed,
}) => {
  const { theme, tenantTheme, whitelabelEnabled, isDarkMode } = useTheme();
  const { isDirty, isPreview } = useReportBuilder();
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(() => ({
    __standard__: false,
    __advanced__: false,
  }));
  const isOnBuilderRoute = /^\/reports\/builder\/.+/.test(location.pathname);
  const isBuilderPreviewByUrl =
    isOnBuilderRoute &&
    new URLSearchParams(location.search).get("mode") === "preview";
  const isBuilderPreviewMode = isBuilderPreviewByUrl || isPreview;

  const menuRoutes = useMemo(() => {
    const collapsedSet = collapsedSections || {};
    return (Array.isArray(routes) ? routes : [])
      .filter((r) => {
        const sectionKey = r?.sectionKey;
        if (sectionKey && collapsedSet[sectionKey] === true) return false;
        return true;
      })
      .map((r) => {
        if (r?.isSectionHeader === true) {
          return { ...r, sectionCollapsed: collapsedSet[r.key] === true };
        }
        return r;
      });
  }, [routes, collapsedSections]);

  const selectedMenuKey = useMemo(
    () => resolveSelectedMenuKey(menuRoutes, location?.pathname, location?.search),
    [menuRoutes, location?.pathname, location?.search],
  );

  useEffect(() => {
    if (isBuilderPreviewMode && unsavedModalOpen) {
      setUnsavedModalOpen(false);
      setPendingKey(null);
    }
  }, [isBuilderPreviewMode, unsavedModalOpen]);

  const tenantLogo = whitelabelEnabled && tenantTheme?.logo ? tenantTheme.logo : null;
  const tenantName = tenantTheme?.name || "Data4Company";

  const handleMenuClick = ({ key }) => {
    const currentKey = `${location.pathname}${location.search || ""}`;
    const resolvePreservedKey = (rawKey) => {
      const asString = String(rawKey || "");
      const [path, queryString = ""] = asString.split("?");
      if (path !== "/reports") return asString;
      const destParams = new URLSearchParams(queryString);
      if (!destParams.has("model")) return asString;
      const currentParams = new URLSearchParams(location.search || "");
      let changed = false;
      for (const paramKey of ["start", "end", "syncId"]) {
        const currentValue = currentParams.get(paramKey);
        if (!destParams.has(paramKey) && currentValue) {
          destParams.set(paramKey, currentValue);
          changed = true;
        }
      }
      if (!changed) return asString;
      const nextQuery = destParams.toString();
      return nextQuery ? `${path}?${nextQuery}` : path;
    };

    const resolvedKey = resolvePreservedKey(key);
    const findRouteByKey = (items, targetKey) => {
      for (const item of items || []) {
        if (item.key === targetKey) return item;
        if (item.children) {
          const found = findRouteByKey(item.children, targetKey);
          if (found) return found;
        }
      }
      return null;
    };
    const clickedRoute = findRouteByKey(routes, key);
    const isParentMenu = Boolean(clickedRoute?.children?.length);

    if (clickedRoute?.isSectionHeader === true) {
      setCollapsedSections((prev) => ({
        ...(prev || {}),
        [clickedRoute.key]: !(prev || {})[clickedRoute.key],
      }));
      return;
    }

    if (isParentMenu) {
      if (collapsed) {
        setCollapsed(false);
      }
      setOpenKeys((prev) =>
        prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
      );
      return;
    }

    if (resolvedKey === currentKey) {
      navigate(0);
      return;
    }
    if (isOnBuilderRoute && !isBuilderPreviewMode && isDirty) {
      setPendingKey(resolvedKey);
      setUnsavedModalOpen(true);
      return;
    }
    navigate(resolvedKey);
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  return (
    <Sider
      width={240}
      collapsed={collapsed}
      collapsedWidth={80}
      trigger={null}
      style={{
        background: theme.sidebarBackground || theme.white,
        position: "fixed",
        left: 0,
        top: 0, // Top 0 pois removemos o header
        height: "100vh",
        zIndex: 1000,
        borderRight: `1px solid ${theme.border}`,
      }}
      data-tour-id="main-menu"
    >
      <Button
        type="text"
        icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
        onClick={() => setCollapsed(!collapsed)}
        style={{
          fontSize: '16px',
          width: 24,
          height: 24,
          position: 'absolute',
          top: 20,
          right: -12,
          zIndex: 1001,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.menuItemText,
          background: theme.sidebarBackground || theme.white,
        }}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "16px 0",
        }}
      >
        {/* Logo e Tenant */}
        <div style={{ 
          padding: collapsed ? null : "16px 8px", 
          display: "flex", 
          alignItems: "center", 
          gap: 12, 
          justifyContent:'center',
          height: collapsed ? 64 : 'auto',
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
           <img
              src={tenantLogo || NovoLogo}
              alt={tenantName}
              onError={(e) => { e.currentTarget.src = d4cLogoFull; }}
              style={{ 
                width: collapsed ? 32 : 96, 
                objectFit: "contain",
                transition: 'all 0.3s ease'
              }}
            />
        </div>
        <Divider style={{ margin: '8px 0 0 0' }} />

        {/* Menu Principal */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <style>{`
            .main-sider-menu .ant-menu-item,
            .main-sider-menu .ant-menu-submenu-title {
              height: 42px !important;
              line-height: 42px !important;
              margin: 2px 0 !important;
              border-radius: 8px !important;
            }
            .main-sider-menu .ant-menu-submenu-title {
              padding-inline-end: 34px !important;
            }
            .main-sider-menu .ant-menu-item .ant-menu-title-content,
            .main-sider-menu .ant-menu-submenu-title .ant-menu-title-content {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-size: 14px;
            }
            .main-sider-menu .ant-menu-sub.ant-menu-inline {
              background: transparent !important;
              padding-left: 10px !important;
            }
            .main-sider-menu .ant-menu-sub.ant-menu-inline .ant-menu-item {
              height: 36px !important;
              line-height: 36px !important;
            }
            .main-sider-menu .ant-menu-submenu-arrow {
              inset-inline-end: 12px !important;
            }
            .ant-layout-sider-collapsed .main-sider-menu.ant-menu-inline-collapsed > .ant-menu-item,
            .ant-layout-sider-collapsed .main-sider-menu.ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title {
              padding-inline: 0 !important;
              justify-content: center !important;
              display:flex !important;
              align-items: center !important;
              justify-content: center !important;
              margin-left: 6px !important;
              }
              .ant-layout-sider-collapsed .main-sider-menu.ant-menu-inline-collapsed .ant-menu-item-icon {
                margin-inline-end: 0 !important;
                }
                .ant-layout-sider-collapsed .main-sider-menu.ant-menu-inline-collapsed .ant-menu-title-content {
              opacity: 0 !important;
              width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              margin-right: -6px !important;

            }
          `}</style>
          <Menu
            className="main-sider-menu"
            mode="inline"
            selectedKeys={[selectedMenuKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            onClick={handleMenuClick}
            items={generateMenuItems(
              menuRoutes,
              collapsed,
              theme,
              selectedMenuKey
            )}
            style={{
              borderRight: 0,
              backgroundColor: "transparent",
              padding: "0 8px",
            }}
          />
        </div>

        <Divider style={{ margin: '8px 0 0 0' }} />

        {/* User Profile */}
        <UserProfile collapsed={collapsed} theme={theme} />
      </div>

      <CustomModal
        title="Sair sem salvar?"
        content="Você possui alterações não salvas. Tem certeza de que deseja sair e perder as alterações?"
        open={!isBuilderPreviewMode && unsavedModalOpen}
        onConfirm={() => {
          setUnsavedModalOpen(false);
          const target = pendingKey;
          setPendingKey(null);
          if (target) navigate(target);
        }}
        onCancel={() => {
          setUnsavedModalOpen(false);
          setPendingKey(null);
        }}
        confirmText="Sair sem salvar"
        cancelText="Cancelar"
        variant="confirmation"
      />
    </Sider>
  );
};

export default SimpleSider;
